import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useState } from 'react';
import Carousel from '@/common/Carousel/Carousel';
import TrackWrapper from '~/components/common/TrackWrapper';
import UserContext, { useFavoriteIds } from '~/context/common/UserContext';
import useCarouselSettings from '~/hooks/home/useCarouselSettings';
import { getNativeVdpPayload } from '~/native/home/utils';
import { fetchSuggestedVehicles } from '~/services/home';
import { SuggestedVehicle } from '~/services/home/contracts/SuggestedVehiclesData';
import { CarouselSkeleton } from '../SkeletonLoaders';
import VehicleDetailsTileSkeleton from '../SkeletonLoaders/CarouselSkeleton/VehicleDetailsTileSkeleton';
import VehicleDetailsTile from '../VehicleDetailsTile/VehicleDetailsTile';

export default function SuggestedForYou() {
  const [suggestedVehicles, setSuggestedVehicles] = useState<
    SuggestedVehicle[] | null
  >(null);
  const [dataWithFavorites, setDataWithFavorites] = useState<
    SuggestedVehicle[] | null
  >(null);

  const {
    location,
    userState: { isLoaded }
  } = useContext(UserContext);
  const zipCode = location.data.zip;
  const favoriteIds = useFavoriteIds();

  useEffectWithController(
    controller => {
      if (!isLoaded || !zipCode) return;

      fetchSuggestedVehicles({ zipCode }, controller).then(data =>
        setSuggestedVehicles(data.vehicles)
      );
    },
    [isLoaded, zipCode]
  );

  useEffect(() => {
    if (!suggestedVehicles) return;

    const data = suggestedVehicles.map(vehicle => {
      const isFavorited = favoriteIds.includes(vehicle.vehicleId);

      return {
        ...vehicle,
        isFavorited
      };
    });

    setDataWithFavorites(data);
  }, [favoriteIds, suggestedVehicles]);

  const { values, carouselRef } = useCarouselSettings({
    heading: 'Suggested for you',
    subHeading: 'Based on recent searches',
    maxLength: suggestedVehicles?.length
  });

  if (!dataWithFavorites)
    return (
      <CarouselSkeleton includeSubHeader Tile={VehicleDetailsTileSkeleton} />
    );

  if (dataWithFavorites.length < 6) return null;

  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes: {
      description: 'Suggested for you',
      isCarousel: true,
      content: {
        vehicleIds: dataWithFavorites.map(vehicle => vehicle.vehicleId)
      }
    }
  };

  return (
    <TrackWrapper event={viewEvent}>
      <Carousel {...values} ref={carouselRef} dataQa="suggested-for-you">
        {dataWithFavorites.map(vehicle => {
          const {
            vehicleId,
            make,
            model,
            price: { total }
          } = vehicle;

          return (
            <a
              href={`/vehicle/${vehicleId}`}
              key={`${make}-${model}`}
              data-analytics-id="Home feed card"
              data-analytics-attributes={JSON.stringify({
                description: 'Suggested for you tile',
                vehicleId
              })}
              data-native-payload={getNativeVdpPayload(vehicleId)}
            >
              <VehicleDetailsTile
                {...vehicle}
                id={vehicleId}
                price={total}
                sectionTitle="Suggested for you"
              />
            </a>
          );
        })}
      </Carousel>
    </TrackWrapper>
  );
}
